// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { startOfToday } from "date-fns";
import {
  and,
  arrayUnion,
  collection,
  collectionGroup,
  connectFirestoreEmulator,
  deleteField,
  doc,
  DocumentData,
  documentId,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  or,
  PartialWithFieldValue,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  waitForPendingWrites,
  where,
  writeBatch,
} from "firebase/firestore";
import { chunk, mapValues, pickBy } from "lodash";
import { makeAutoObservable } from "mobx";
import { ZodError } from "zod";

import { isOfflineMode } from "~client-env-utils";
import {
  ClientRecord,
  clientRecordSchema,
  LocationRecord,
  locationRecordSchema,
  OpportunityType,
  ParoleBoardClientProfileRecord,
  paroleBoardClientProfileRecordSchema,
  WorkflowsResidentRecord,
  workflowsResidentRecordSchema,
} from "~datatypes";
import {
  FIRESTORE_GENERAL_COLLECTION_MAP,
  FirestoreCollectionKey,
} from "~firestore-config";

import { fetchFirebaseToken } from "../api/fetchFirebaseToken";
import type RootStore from "../RootStore";
import { UserAppMetadata } from "../RootStore/types";
import {
  Opportunity,
  OpportunityTab,
  OpportunityTabGroup,
  UsTnExpirationOpportunity,
} from "../WorkflowsStore";
import { CollectionDocumentSubscription } from "../WorkflowsStore/subscriptions";
import { getMonthYearFromDate } from "../WorkflowsStore/utils";
import { collectionNameForCurrentEnv } from "./collectionNames";
import {
  AdjudicationStatusValue,
  AtLeastOneTrue,
  AutoSnoozeUpdate,
  ClientAddressUpdate,
  ClientOpportunityUpdateRecord,
  clientOpportunityUpdatesSchema,
  ContactMethodType,
  CustomTaskCreateInput,
  customTaskCreatePayloadSchema,
  CustomTaskUpdateInput,
  ExternalSystemRequestStatus,
  FormUpdate,
  ManualSnoozeUpdate,
  MilestonesMessage,
  OfficerRequest,
  OpportunityUpdateWithForm,
  PersonUpdateType,
  SupervisionTaskUpdate,
  UserUpdateRecord,
  UsTnExpirationOpportunityUpdate,
} from "./types";

// Firestore's "in" operator supports at most 10 comparison values per query
const FIRESTORE_IN_QUERY_BATCH_SIZE = 10;

/** Server-side inequality range for a `getResidentsForState` query: matches
 * documents whose `field` falls between `startDateInclusive` and
 * `endDateInclusive`, compared as plain strings. */
export type FirestoreDateRangeFilter = {
  field: string;
  startDateInclusive: string;
  endDateInclusive: string;
};

/**
 * The field paths a profile failed to parse at. A ZodError's own message can
 * include the values it received, which may be PII, so profile parse errors
 * report only these paths. No document id either, since profile document ids
 * carry the resident's DOC id.
 *
 * @param error - The error from parsing a profile document.
 */
function profileParseErrorPaths(error: ZodError): string {
  return error.issues.map((issue) => issue.path.join(".")).join(", ");
}

/**
 * Parses each resident document, skipping and logging a malformed one so it
 * can't take down the whole result set.
 */
function parseResidentRecords(
  docs: Array<QueryDocumentSnapshot>,
): WorkflowsResidentRecord[] {
  return docs.flatMap((result) => {
    const parsed = workflowsResidentRecordSchema.safeParse({
      ...result.data(),
      recordId: result.id,
    });
    if (!parsed.success) {
      console.error(
        `Failed to parse resident record [${result.id}]: ${parsed.error}`,
      );
      return [];
    }
    return [parsed.data];
  });
}

/**
 * Parses each profile document, skipping and logging a malformed one so it
 * can't take down a whole docket.
 */
function parseParoleBoardClientProfiles(
  docs: Array<QueryDocumentSnapshot>,
): ParoleBoardClientProfileRecord[] {
  return docs.flatMap((result) => {
    const parsed = paroleBoardClientProfileRecordSchema.safeParse(
      result.data(),
    );
    if (!parsed.success) {
      console.error(
        `Failed to parse a Parole Board client profile at fields [${profileParseErrorPaths(parsed.error)}]`,
      );
      return [];
    }
    return [parsed.data];
  });
}

export default class FirestoreStore {
  rootStore;

  private db: Firestore;

  constructor({ rootStore }: { rootStore: typeof RootStore }) {
    makeAutoObservable(this, {
      useOfflineFirestore: false,
      rootStore: false,
      currentUserUid: false,
    });

    this.rootStore = rootStore;

    this.db = getFirestore(this.rootStore.firebaseAuthClient.app);

    if (this.useOfflineFirestore) {
      connectFirestoreEmulator(this.db, "localhost", 8080);
    }
  }

  get useOfflineFirestore() {
    // demo- is a Firebase-reserved prefix that will only work with local emulators
    return this.rootStore.firebaseAuthClient.projectId.startsWith("demo-");
  }

  /**
   * The signed-in Firebase user's uid (for staff, their email address), or
   * undefined before sign-in.
   */
  get currentUserUid(): string | undefined {
    return this.rootStore.firebaseAuthClient.auth.currentUser?.uid;
  }

  /* Authenticate with Firebase for users with based on metadata */
  async authenticate(
    auth0Token: string,
    appMetadata: UserAppMetadata,
    impersonatedEmail?: string,
  ): Promise<void> {
    const shouldGenerateToken =
      appMetadata.stateCode === "recidiviz" ||
      isOfflineMode() ||
      appMetadata.routes?.workflowsSupervision ||
      appMetadata.routes?.workflowsFacilities ||
      appMetadata.routes?.tasks ||
      appMetadata.routes?.parole;

    if (shouldGenerateToken) {
      const impersonationParams = impersonatedEmail
        ? {
            impersonatedEmail,
            impersonatedStateCode: appMetadata.stateCode,
          }
        : undefined;

      let firebaseToken;
      try {
        firebaseToken = await fetchFirebaseToken(
          auth0Token,
          impersonationParams,
        );
      } catch (e) {
        throw new Error(
          `Error in fetchFirebaseToken for ${appMetadata.userHash}: ${e}`,
        );
      }

      await this.rootStore.firebaseAuthClient.authenticate(
        firebaseToken,
        this.useOfflineFirestore ? "http://localhost:9099" : undefined,
      );
    }
  }

  collectionName(collectionKey: FirestoreCollectionKey) {
    return collectionNameForCurrentEnv(
      collectionKey,
      this.rootStore.userStore.userAppMetadata,
    );
  }

  collection(collectionKey: FirestoreCollectionKey) {
    return collection(this.db, this.collectionName(collectionKey));
  }

  doc(collectionKey: FirestoreCollectionKey, ...paths: string[]) {
    return doc(this.db, this.collectionName(collectionKey), ...paths);
  }

  async getClient(
    clientId: string,
    stateCode: string,
  ): Promise<ClientRecord | undefined> {
    // TODO(#1763) index clients by pseudo ID and go back to a simple getDoc lookup
    const results = await getDocs(
      query(
        this.collection({ key: "clients" }),
        where("pseudonymizedId", "==", clientId),
        where("stateCode", "==", stateCode),
        limit(1),
      ),
    );

    const result = results.docs[0];
    if (result?.exists())
      return clientRecordSchema.parse({
        ...result.data(),
        recordId: result.id,
      });
  }

  async getClientsForOfficerId(
    stateCode: string,
    officerExternalId: string,
  ): Promise<ClientRecord[]> {
    const results = await getDocs(
      query(
        this.collection({ key: "clients" }),
        where("stateCode", "==", stateCode),
        where("officerId", "==", officerExternalId),
      ),
    );

    return results.docs.map((result) =>
      clientRecordSchema.parse({
        ...result.data(),
        recordId: result.id,
      }),
    );
  }

  async getOpportunityUpdatesForReviewerId(
    stateCode: string,
    reviewerId: string,
  ): Promise<ClientOpportunityUpdateRecord[]> {
    const results = await getDocs(
      query(
        collectionGroup(
          this.db,
          FIRESTORE_GENERAL_COLLECTION_MAP.clientOpportunityUpdates,
        ),
        where("stateCode", "==", stateCode),
        where("currentReviewerId", "==", reviewerId),
      ),
    );

    return results.docs.map((result) =>
      clientOpportunityUpdatesSchema.parse({
        ...result.data(),
        clientRecordId: result.ref.parent.parent?.id,
      }),
    );
  }

  async getOpportunityUpdatesByAllUniqueReviewerIds(
    stateCode: string,
    reviewerId: string,
  ): Promise<ClientOpportunityUpdateRecord[]> {
    const results = await getDocs(
      query(
        collectionGroup(
          this.db,
          FIRESTORE_GENERAL_COLLECTION_MAP.clientOpportunityUpdates,
        ),
        where("stateCode", "==", stateCode),
        where("allUniqueReviewerIds", "array-contains", reviewerId),
      ),
    );

    return results.docs.map((result) =>
      clientOpportunityUpdatesSchema.parse({
        ...result.data(),
        clientRecordId: result.ref.parent.parent?.id,
      }),
    );
  }

  async getClientsForRecordIds(
    recordIds: string[],
    stateCode: string,
  ): Promise<ClientRecord[]> {
    if (recordIds.length === 0) return [];
    const batches = await Promise.all(
      chunk(recordIds, FIRESTORE_IN_QUERY_BATCH_SIZE).map((batch) =>
        getDocs(
          query(
            this.collection({ key: "clients" }),
            where("stateCode", "==", stateCode),
            where(documentId(), "in", batch),
          ),
        ),
      ),
    );
    return batches.flatMap((results) =>
      results.docs.map((result) =>
        clientRecordSchema.parse({
          ...result.data(),
          recordId: result.id,
        }),
      ),
    );
  }

  async getResident(
    residentId: string,
    stateCode: string,
  ): Promise<WorkflowsResidentRecord | undefined> {
    // TODO(#1763) index clients by pseudo ID and go back to a simple getDoc lookup
    const results = await getDocs(
      query(
        this.collection({ key: "residents" }),
        where("pseudonymizedId", "==", residentId),
        where("stateCode", "==", stateCode),
        limit(1),
      ),
    );

    const result = results.docs[0];
    if (result?.exists())
      return workflowsResidentRecordSchema.parse({
        ...result.data(),
        recordId: result.id,
      });
  }

  async getResidentByPersonExternalId(
    stateCode: string,
    personExternalId: string,
  ): Promise<WorkflowsResidentRecord | undefined> {
    const results = await getDocs(
      query(
        this.collection({ key: "residents" }),
        where("personExternalId", "==", personExternalId),
        where("stateCode", "==", stateCode),
        limit(1),
      ),
    );

    // Unlike getClient/getResident above, most people queried this way have no
    // matching Resident doc at all (most clients are not currently incarcerated),
    // so the no-match case must resolve to undefined rather than throw.
    const result = results.docs[0];
    if (result?.exists()) {
      return workflowsResidentRecordSchema.parse({
        ...result.data(),
        recordId: result.id,
      });
    }
  }

  /** Returns every resident for a state, so a single malformed doc shouldn't
   * take down the whole result set -- skip and log it instead of letting
   * parse() throw. When `dateRange` is given, the query itself is scoped to
   * residents whose `dateRange.field` falls within `startDateInclusive`/
   * `endDateInclusive` (both compared as plain strings, per that field's own
   * schema); omit it to return every resident in the state, unfiltered.
   **/
  async getResidentsForState(
    stateCode: string,
    dateRange?: FirestoreDateRangeFilter,
  ): Promise<WorkflowsResidentRecord[]> {
    const constraints = [where("stateCode", "==", stateCode)];
    if (dateRange) {
      constraints.push(
        where(dateRange.field, ">=", dateRange.startDateInclusive),
        where(dateRange.field, "<=", dateRange.endDateInclusive),
      );
    }

    const results = await getDocs(
      query(this.collection({ key: "residents" }), and(...constraints)),
    );

    return parseResidentRecords(results.docs);
  }

  /**
   * Returns every resident in these facilities. A malformed doc is skipped
   * and logged, as in getResidentsForState.
   *
   * @param facilityIds - Facility ids, matched against each resident's
   *   facilityId.
   * @param stateCode - The residents' state code.
   */
  async getResidentsForFacilities(
    facilityIds: string[],
    stateCode: string,
  ): Promise<WorkflowsResidentRecord[]> {
    if (facilityIds.length === 0) return [];
    const batches = await Promise.all(
      chunk(facilityIds, FIRESTORE_IN_QUERY_BATCH_SIZE).map((batch) =>
        getDocs(
          query(
            this.collection({ key: "residents" }),
            where("stateCode", "==", stateCode),
            where("facilityId", "in", batch),
          ),
        ),
      ),
    );
    return batches.flatMap((results) => parseResidentRecords(results.docs));
  }

  /**
   * Returns the incarceration facilities in a state, from the locations
   * collection. A malformed doc is skipped and logged, as in
   * getResidentsForState, so it can't empty the whole facility list.
   *
   * @param stateCode - The state whose facilities to return.
   */
  async getIncarcerationFacilities(
    stateCode: string,
  ): Promise<LocationRecord[]> {
    const results = await getDocs(
      query(
        this.collection({ key: "locations" }),
        where("stateCode", "==", stateCode),
        where("idType", "==", "facilityId"),
      ),
    );
    return results.docs.flatMap((result) => {
      const parsed = locationRecordSchema.safeParse(result.data());
      if (!parsed.success) {
        console.error(
          `Failed to parse location record [${result.id}]: ${parsed.error}`,
        );
        return [];
      }
      return [parsed.data];
    });
  }

  /**
   * Returns the resident's Parole Board client profile, or undefined when
   * they have none. Firestore rules deny the read to users without Parole
   * Board access, so this throws for them rather than returning undefined.
   *
   * @param pseudonymizedId - The resident's pseudonymizedId.
   * @param stateCode - The resident's state code.
   */
  async getParoleBoardClientProfile(
    pseudonymizedId: string,
    stateCode: string,
  ): Promise<ParoleBoardClientProfileRecord | undefined> {
    const results = await getDocs(
      query(
        this.collection({ key: "paroleBoardClientProfiles" }),
        where("pseudonymizedId", "==", pseudonymizedId),
        where("stateCode", "==", stateCode),
        limit(1),
      ),
    );

    const [result] = results.docs;
    if (!result) return undefined;

    // Unlike the docket, which skips a malformed profile, this fails the case
    // page: a profile missing the fields that failed could mislead the Board.
    const parsed = paroleBoardClientProfileRecordSchema.safeParse(
      result.data(),
    );
    if (!parsed.success) {
      throw new Error(
        `Failed to parse the Parole Board client profile at fields [${profileParseErrorPaths(parsed.error)}]`,
      );
    }
    return parsed.data;
  }

  /**
   * Returns every Parole Board client profile in a state. Throws for users
   * without Parole Board access, as getParoleBoardClientProfile does.
   *
   * @param stateCode - The state whose profiles to return.
   */
  async getParoleBoardClientProfilesForState(
    stateCode: string,
  ): Promise<ParoleBoardClientProfileRecord[]> {
    const results = await getDocs(
      query(
        this.collection({ key: "paroleBoardClientProfiles" }),
        where("stateCode", "==", stateCode),
      ),
    );
    return parseParoleBoardClientProfiles(results.docs);
  }

  /**
   * Returns the Parole Board client profiles stored under these document ids,
   * which match the residents collection's. An id with no profile is
   * skipped. Throws for users without Parole Board access, as
   * getParoleBoardClientProfile does.
   *
   * @param recordIds - Resident record ids (WorkflowsResidentRecord.recordId).
   * @param stateCode - The residents' state code.
   */
  async getParoleBoardClientProfilesByRecordIds(
    recordIds: string[],
    stateCode: string,
  ): Promise<ParoleBoardClientProfileRecord[]> {
    if (recordIds.length === 0) return [];
    const batches = await Promise.all(
      chunk(recordIds, FIRESTORE_IN_QUERY_BATCH_SIZE).map((batch) =>
        getDocs(
          query(
            this.collection({ key: "paroleBoardClientProfiles" }),
            where("stateCode", "==", stateCode),
            where(documentId(), "in", batch),
          ),
        ),
      ),
    );
    return batches.flatMap((results) =>
      parseParoleBoardClientProfiles(results.docs),
    );
  }

  private get userUpdatesKey() {
    const email = this.rootStore.userStore.user?.email?.toLowerCase();
    if (!email) {
      throw new Error("Trying to access user updates without a user email");
    }
    return email;
  }

  buildUserUpdatesSubscription() {
    return new CollectionDocumentSubscription<UserUpdateRecord>(
      this,
      { key: "userUpdates" },
      this.userUpdatesKey,
    );
  }

  /**
   * All updates to userUpdates in Firestore should utilitize this method, which first
   * sets the user email to all lowercase.
   */
  updateUserUpdatesDocument(update: PartialWithFieldValue<UserUpdateRecord>) {
    const doc = this.doc({ key: "userUpdates" }, this.userUpdatesKey);
    return this.updateDocument(doc, update);
  }

  /**
   * All updates to Firestore should utilitize this method, which first checks that the user is an impersonator.
   * Do not use the Firestore setDoc directly.
   */
  updateDocument(docRef: DocumentReference<DocumentData>, update: any) {
    if (
      this.rootStore.isImpersonating &&
      import.meta.env.VITE_DEPLOY_ENV === "production"
    ) {
      // eslint-disable-next-line
      console.log(
        `[IMPERSONATOR IN PROD] Skipping update for: ${docRef.path} with updates ${JSON.stringify(
          update,
        )}`,
      );
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    return setDoc(docRef, { ...update }, { merge: true });
  }

  /**
   * All updates to the `clientUpdatesV2` collection should utilitize this method, which first adds the `stateCode` to the person document,
   * then updates the update document.
   */
  async updateClientUpdatesV2Document(
    recordId: string,
    docRef: DocumentReference<DocumentData>,
    update: any,
  ) {
    if (
      this.rootStore.isImpersonating &&
      import.meta.env.VITE_DEPLOY_ENV === "production"
    ) {
      // eslint-disable-next-line no-console
      console.log(
        `[IMPERSONATOR IN PROD] Skipping update for: ${docRef.path} with updates ${JSON.stringify(
          update,
        )}`,
      );
      return;
    }

    if (
      this.rootStore.userStore.isRecidivizUser &&
      import.meta.env.VITE_DEPLOY_ENV === "production"
    ) {
      // eslint-disable-next-line no-console
      console.log(
        `Recidiviz user in prod; Skipping update for: ${docRef.path} with updates ${JSON.stringify(
          update,
        )}`,
      );
      return;
    }

    // Batching might speed things up by one round trip, but it also lets us waitForPendingUpdates without
    // possibly missing the second update.
    const batch = writeBatch(this.db);
    // Adding the stateCode ensures that the person document actually exists. See #3845
    const personDocRef = this.doc({ key: "clientUpdatesV2" }, `${recordId}`);
    batch.set(
      personDocRef,
      { stateCode: recordId.slice(0, 5) },
      { merge: true },
    );
    batch.set(docRef, { ...update }, { merge: true });
    await batch.commit();
  }

  // Function to add an update in `clientUpdatesV2`. All JusticeInvolvedPerson updates (both Clients and Residents)
  // are being stored in `clientUpdatesv2`, so the name of the collection is misleading, all person updates are stored here.
  async updatePerson(
    recordId: string,
    update:
      | Record<PersonUpdateType, string | ContactMethodType>
      | Record<"stateCode", string>,
  ) {
    const docRef = this.doc({ key: "clientUpdatesV2" }, `${recordId}`);

    // This is the only place an update should be made to `clientUpdatesV2` without using
    // the `updateClientUpdatesV2Document` method. Because the update is written directly to
    // the person document, include the `stateCode` so the document is properly populated,
    // matching the behavior of `updateClientUpdatesV2Document`. See #3845.
    return this.updateDocument(docRef, {
      stateCode: recordId.slice(0, 5),
      ...update,
    });
  }

  /**
   * Returns a CollectionReference for the per-person `custom_tasks` subcollection
   * at `clientUpdatesV2/{recordId}/custom_tasks`, honoring the current env prefix
   * (DEMO_/DEMO2_).
   */
  customTasksCollection(recordId: string) {
    return collection(
      this.db,
      this.collectionName({ key: "clientUpdatesV2" }),
      recordId,
      FIRESTORE_GENERAL_COLLECTION_MAP.customTasks,
    );
  }

  /**
   * Creates a new custom task under `clientUpdatesV2/{recordId}/custom_tasks/{taskId}`.
   * `taskId` is a client-generated UUID v4. `createdOn` is stamped with
   * `serverTimestamp()`. Returns the new task id.
   */
  async createCustomTask(
    recordId: string,
    input: CustomTaskCreateInput,
  ): Promise<string> {
    const taskId = crypto.randomUUID();
    const taskDocRef = this.doc(
      { key: "clientUpdatesV2" },
      `${recordId}/${FIRESTORE_GENERAL_COLLECTION_MAP.customTasks}/${taskId}`,
    );

    // Run the create payload through the Zod schema so every defaulted
    // field on `customTaskSchema` (e.g. `deletedOn: null`) automatically
    // lands in the Firestore write — without the create site having to
    // know which fields are defaulted. `customTaskCreatePayloadSchema`
    // omits the server-stamped timestamps so the FieldValue sentinel for
    // `createdOn` doesn't have to pass the Timestamp validator; we layer
    // it on after the parse.
    const payload = customTaskCreatePayloadSchema.parse({
      id: taskId,
      title: input.title,
      dueDate: input.dueDate,
      // Pass `recurrence` through explicitly so a one-off task (caller
      // passes `null`) lands as `recurrence: null` and a recurring task
      // lands with the RRULE string. The schema's `.default(null)` also
      // covers the case where caller omits the field entirely.
      recurrence: input.recurrence ?? null,
      stateCode: recordId.slice(0, 5),
    });

    await this.updateClientUpdatesV2Document(recordId, taskDocRef, {
      ...payload,
      createdOn: serverTimestamp(),
    });

    return taskId;
  }

  /**
   * Partial-merge update for a custom task. Always stamps `updatedOn`.
   * Callers are responsible for stamping `completedOn` on a completion
   * transition, or passing it as `null` when reopening a completed task.
   */
  async updateCustomTask(
    recordId: string,
    taskId: string,
    patch: CustomTaskUpdateInput,
  ): Promise<void> {
    const taskDocRef = this.doc(
      { key: "clientUpdatesV2" },
      `${recordId}/${FIRESTORE_GENERAL_COLLECTION_MAP.customTasks}/${taskId}`,
    );

    return this.updateClientUpdatesV2Document(recordId, taskDocRef, {
      ...patch,
      updatedOn: serverTimestamp(),
    });
  }

  /**
   * Soft-deletes a custom task by stamping `deletedOn`.
   */
  async softDeleteCustomTask(recordId: string, taskId: string): Promise<void> {
    const taskDocRef = this.doc(
      { key: "clientUpdatesV2" },
      `${recordId}/${FIRESTORE_GENERAL_COLLECTION_MAP.customTasks}/${taskId}`,
    );

    return this.updateClientUpdatesV2Document(recordId, taskDocRef, {
      deletedOn: serverTimestamp(),
    });
  }

  async updateSupervisionTask(recordId: string, update: SupervisionTaskUpdate) {
    const taskDocRef = this.doc(
      { key: "clientUpdatesV2" },
      `${recordId}/${FIRESTORE_GENERAL_COLLECTION_MAP.taskUpdates}/supervision`,
    );

    return this.updateClientUpdatesV2Document(recordId, taskDocRef, {
      ...update,
    });
  }

  async updateMilestonesMessages(
    recordId: string,
    update: PartialWithFieldValue<MilestonesMessage>,
  ) {
    const dateKey = `milestones_${getMonthYearFromDate(startOfToday())}`;
    const taskDocRef = this.doc(
      { key: "clientUpdatesV2" },
      `${recordId}/${FIRESTORE_GENERAL_COLLECTION_MAP.milestonesMessages}/${dateKey}`,
    );

    return this.updateClientUpdatesV2Document(recordId, taskDocRef, update);
  }

  async updateAddressUpdates(
    recordId: string,
    update: PartialWithFieldValue<ClientAddressUpdate>,
  ) {
    const addressDocRef = this.doc({ key: "clientUpdatesV2" }, recordId);

    return this.updateClientUpdatesV2Document(recordId, addressDocRef, {
      addressUpdate: update,
    });
  }

  async updateForm(
    recordId: string,
    update: PartialWithFieldValue<FormUpdate<Record<string, any>>>,
    formId: string,
  ) {
    const taskDocRef = this.doc(
      { key: "clientUpdatesV2" },
      `${recordId}/${FIRESTORE_GENERAL_COLLECTION_MAP.clientFormUpdates}/${formId}`,
    );

    return this.updateClientUpdatesV2Document(recordId, taskDocRef, update);
  }

  async updateOpportunity(
    opportunity: Opportunity,
    update: PartialWithFieldValue<
      OpportunityUpdateWithForm<Record<string, any>>
    >,
  ) {
    const {
      person: { recordId: personRecordId, stateCode },
      firestoreUpdateDocId,
    } = opportunity;

    const opportunityDocRef = this.doc(
      { key: "clientUpdatesV2" },
      `${personRecordId}/${FIRESTORE_GENERAL_COLLECTION_MAP.clientOpportunityUpdates}/${firestoreUpdateDocId}`,
    );

    return this.updateClientUpdatesV2Document(
      personRecordId,
      opportunityDocRef,
      {
        stateCode,
        ...update,
      },
    );
  }

  async updateOpportunitySubmitted(
    userEmail: string,
    opportunity: Opportunity,
    stateCode: string,
    subcategory?: string,
  ) {
    // firestore rejects undefined values, so filter them out
    const update = pickBy({
      by: userEmail,
      date: serverTimestamp(),
      subcategory: subcategory,
    });
    return this.updateOpportunity(opportunity, {
      submitted: update,
      stateCode: stateCode,
    });
  }

  async updateOpportunityAutoSnooze(
    opportunity: Opportunity,
    snoozeUpdate: AutoSnoozeUpdate,
    deleteSnoozeField: boolean,
  ): Promise<void> {
    const changes = deleteSnoozeField
      ? { autoSnooze: deleteField(), stateCode: opportunity.person.stateCode }
      : {
          autoSnooze: { ...snoozeUpdate },
          stateCode: opportunity.person.stateCode,
        };

    await this.updateSnoozeCompanions(opportunity, changes);
    return this.updateOpportunity(opportunity, changes);
  }

  async deleteOpportunityActionHistory(opportunity: Opportunity) {
    return this.updateOpportunity(opportunity, {
      actionHistory: deleteField(),
      allUniqueReviewerIds: deleteField(),
      currentReviewerId: deleteField(),
      stateCode: opportunity.person.stateCode,
    });
  }

  async updateOpportunityActionHistory({
    opportunity,
    actionHistory,
    currentReviewerId,
    currentUserId,
    stateCode,
  }: {
    opportunity: Opportunity;
    actionHistory: OfficerRequest[];
    currentReviewerId?: string;
    currentUserId?: string;
    stateCode?: string;
  }): Promise<void> {
    const update = {
      actionHistory,
      currentReviewerId: currentReviewerId ?? deleteField(),
      stateCode: stateCode ?? opportunity.person.stateCode,
      ...(currentUserId && {
        allUniqueReviewerIds: arrayUnion(currentUserId),
      }),
    };
    return this.updateOpportunity(opportunity, update);
  }

  async updateOpportunityManualSnooze(
    opportunity: Opportunity,
    snoozeUpdate: ManualSnoozeUpdate,
    deleteSnoozeField: boolean,
  ): Promise<void> {
    const changes = deleteSnoozeField
      ? { manualSnooze: deleteField(), stateCode: opportunity.person.stateCode }
      : {
          manualSnooze: { ...snoozeUpdate },
          stateCode: opportunity.person.stateCode,
        };

    await this.updateSnoozeCompanions(opportunity, changes);
    return this.updateOpportunity(opportunity, changes);
  }

  async updateSnoozeCompanions(
    opportunity: Opportunity,
    changes: Record<string, unknown>,
  ): Promise<void[] | undefined> {
    if (!opportunity.snoozeCompanionOpportunities?.length) {
      return;
    }

    return Promise.all(
      opportunity.snoozeCompanionOpportunities.map((opp: Opportunity) =>
        this.updateOpportunity(opp, changes),
      ),
    );
  }

  async updateOpportunityDenial(
    userEmail: string,
    opportunity: Opportunity,
    stateCode: string,
    fieldUpdates: {
      reasons?: string[];
      otherReason?: string;
      userInput?: Record<string, string>;
    },
    deleteFields?: {
      otherReason: boolean;
      userInput: boolean;
    },
  ): Promise<void> {
    // Firestore will reject any undefined values so filter them out
    const filteredUpdates = pickBy(fieldUpdates);

    const fieldsToDelete = mapValues(pickBy(deleteFields), () => deleteField());

    const changes = {
      denial: {
        ...filteredUpdates,
        ...fieldsToDelete,
        updated: {
          by: userEmail,
          date: serverTimestamp(),
        },
      },
      stateCode: stateCode,
    };

    await this.updateSnoozeCompanions(opportunity, changes);
    return this.updateOpportunity(opportunity, changes);
  }

  async updateOpportunityCompleted(
    userEmail: string,
    opportunity: Opportunity,
    clearCompletion = false,
  ): Promise<void> {
    return this.updateOpportunity(opportunity, {
      completed: clearCompletion
        ? deleteField()
        : {
            update: {
              by: userEmail,
              date: serverTimestamp(),
            },
          },
      stateCode: opportunity.person.stateCode,
    });
  }

  async updateAdjudicationStatus(
    userEmail: string,
    opportunity: Opportunity,
    status: AdjudicationStatusValue,
  ): Promise<void> {
    return this.updateOpportunity(opportunity, {
      adjudicationStatus: {
        adjudicationStatus: status,
        by: userEmail,
        date: serverTimestamp(),
      },
    });
  }

  async deleteAdjudicationStatus(opportunity: Opportunity): Promise<void> {
    return this.updateOpportunity(opportunity, {
      adjudicationStatus: deleteField(),
    });
  }

  async updateOpportunityLastViewed(
    userEmail: string,
    opportunity: Opportunity,
  ): Promise<void> {
    return this.updateOpportunity(opportunity, {
      lastViewed: { by: userEmail, date: serverTimestamp() },
    });
  }

  updateSelectedSearchIds(
    selectedSearchIds: string[],
  ): Promise<void> | undefined {
    return this.updateUserUpdatesDocument({
      stateCode: this.rootStore.tenantStore.currentTenantId,
      selectedSearchIds,
      searchAllCaseloads: false,
    });
  }

  // Leaves selectedSearchIds alone so pages without an all-caseloads option
  // can fall back to the user's last individual selection.
  updateSearchAllCaseloads(): Promise<void> | undefined {
    return this.updateUserUpdatesDocument({
      stateCode: this.rootStore.tenantStore.currentTenantId,
      searchAllCaseloads: true,
    });
  }

  updateDismissedOpportunityNotificationIds(
    dismissedOpportunityNotificationIds: string[],
  ) {
    return this.updateUserUpdatesDocument({
      dismissedOpportunityNotificationIds,
    });
  }

  async updateCustomTabOrderings(
    opportunityToUpdate: OpportunityType,
    tabGroupToUpdate: OpportunityTabGroup,
    customTabOrdering: OpportunityTab[],
  ) {
    // Add the new opportunity-ordering pair to the existing record
    const doc = this.doc({ key: "userUpdates" }, this.userUpdatesKey);
    const previousCustomOrderings =
      (await getDoc(doc)).get("customTabOrderings") ?? {};
    const customTabOrderings = {
      ...previousCustomOrderings,
      [opportunityToUpdate]: {
        ...previousCustomOrderings[opportunityToUpdate],
        [tabGroupToUpdate]: customTabOrdering,
      },
    };
    return this.updateUserUpdatesDocument({
      customTabOrderings,
    });
  }

  async updateListViewPreference(showListView: boolean) {
    return this.updateUserUpdatesDocument({
      showListView,
    });
  }

  async updateClientsResidentsViewPreference(showListView: boolean) {
    return this.updateUserUpdatesDocument({
      clientsResidentsShowListView: showListView,
    });
  }

  async updateUsTnExpirationContactNoteStatus(
    opportunity: UsTnExpirationOpportunity,
    currentUserEmail: string,
    contactNote: Record<number, string[]>,
    submittedTimestamp: Timestamp,
    status: ExternalSystemRequestStatus,
    error?: string,
  ) {
    // Ignore recidiviz and non-state users in prod
    if (
      import.meta.env.VITE_DEPLOY_ENV === "production" &&
      opportunity.rootStore.userStore.stateCode !== opportunity.person.stateCode
    )
      return;

    const contactNoteUpdate: UsTnExpirationOpportunityUpdate = {
      contactNote: {
        status,
        submitted: {
          by: currentUserEmail,
          date: submittedTimestamp,
        },
        note: contactNote,
        noteStatus: {},
        ...(error !== undefined && { error }),
      },
    };

    return this.updateOpportunity(opportunity, {
      ...contactNoteUpdate,
      stateCode: opportunity.person.stateCode,
    });
  }

  async updateOmsSnoozeStatus(
    opportunity: Opportunity,
    currentUserEmail: string,
    userStateCode: string,
    recordId: string,
    snoozeUntil: string,
    submittedTimestamp: Timestamp,
    status: ExternalSystemRequestStatus,
    error?: string,
  ) {
    // Ignore recidiviz and non-state users in prod
    if (
      import.meta.env.VITE_DEPLOY_ENV === "production" &&
      userStateCode !== opportunity.person.stateCode
    )
      return;

    return this.updateOpportunity(opportunity, {
      omsSnooze: {
        status,
        submitted: {
          by: currentUserEmail,
          date: submittedTimestamp,
        },
        snoozeUntil,
        ...(error !== undefined && { error }),
      },
      stateCode: opportunity.person.stateCode,
    });
  }

  async deleteOpportunitySubmitted(opportunity: Opportunity) {
    return this.updateOpportunity(opportunity, {
      submitted: deleteField(),
      stateCode: opportunity.person.stateCode,
    });
  }

  async deleteOpportunityDenialAndSnooze(opportunity: Opportunity) {
    const changes = {
      denial: deleteField(),
      autoSnooze: deleteField(),
      manualSnooze: deleteField(),
      stateCode: opportunity.person.stateCode,
    };
    await this.updateSnoozeCompanions(opportunity, changes);
    return this.updateOpportunity(opportunity, changes);
  }

  async getOpportunitiesForJIIAndOpportunityType(
    personExternalId: string,
    opportunityTypeCollection: string,
    stateCode: string,
    byEligibilityStatus: AtLeastOneTrue<{
      includeEligible: boolean;
      includeAlmostEligible: boolean;
      includeIneligible: boolean;
    }>,
  ): Promise<DocumentData[]> {
    const eligibilityConstraints = [];
    if (byEligibilityStatus.includeEligible)
      eligibilityConstraints.push(where("isEligible", "==", true));
    if (byEligibilityStatus.includeAlmostEligible)
      eligibilityConstraints.push(where("isAlmostEligible", "==", true));
    if (byEligibilityStatus.includeIneligible)
      eligibilityConstraints.push(
        and(
          where("isEligible", "==", false),
          where("isAlmostEligible", "==", false),
        ),
      );

    // This should never happen because the AtLeastOneTrue type prevents
    // calling this method without at least one status included
    // as true, but we add this check just to be safe.
    if (!eligibilityConstraints.length) {
      throw new Error(
        "At least one eligibility status must be included to query opportunities",
      );
    }

    const results = await getDocs(
      query(
        this.collection({ raw: opportunityTypeCollection }),
        and(
          where("externalId", "==", personExternalId),
          where("stateCode", "==", stateCode),
          or(...eligibilityConstraints),
        ),
      ),
    );

    return results.docs.map((result) => result.data());
  }

  waitForPendingUpdates(): Promise<void> {
    return waitForPendingWrites(this.db);
  }
}
